import React, { useMemo, useState } from "react";
import { ArrowLeft, CircleAlert, Pencil, Trash2, Undo2 } from "lucide-react";
import { compactPrice } from "@/helpers/pastClientHomes";
import { sampleLine } from "@/components/CsvColumnPreview";
import {
  useRemoveImportRow,
  useSelectImportRows,
  useUpdateImportRow,
} from "@/hooks/useImportBatches";
import "./ProspectImportReview.scss";

const PAGE = 100;

const plural = (n, one, many) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

const groupOf = (row) => (row.home ? "homes" : row.clientOnly ? "clients" : row.type === "buyer" ? "buyers" : "sellers");

const GROUPS = [
  { key: "homes", title: "Past buyers' properties", becomes: "Each becomes a prospect property" },
  { key: "sellers", title: "Sellers and owners", becomes: "Each becomes a prospect property" },
  { key: "buyers", title: "Buyers", becomes: "Each becomes a draft Buyer Want-Listing in Ready to review" },
  { key: "clients", title: "Contacts only", becomes: "Each is added to My Clients" },
];

const homeLine = (view) =>
  [
    [view?.city, view?.state, view?.zip].filter(Boolean).join(" "),
    view?.purchaseDate ? `Bought ${String(view.purchaseDate).slice(0, 4)}` : null,
    view?.yearsOwned != null ? `${plural(view.yearsOwned, "year", "years")} ago` : null,
    view?.purchasePrice ? `for ${compactPrice(view.purchasePrice)}` : null,
    view?.bedrooms ? `${view.bedrooms} bd` : null,
    view?.bathrooms ? `${view.bathrooms} ba` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const titleOf = (row) => (row.home ? row.home.view?.street || row.home.street : row.name || `Row ${row.row}`);

const lineOf = (row) => {
  if (row.home) return homeLine(row.home.view);
  if (row.clientOnly) return [row.contact?.email, row.contact?.phone].filter(Boolean).join(" · ");
  return sampleLine(row);
};

const citiesText = (cities) => (cities || []).map((c) => [c.city, c.state].filter(Boolean).join(", ")).join("; ");

const formOf = (row) => ({
  firstName: row.contact?.firstName || "",
  lastName: row.contact?.lastName || "",
  email: row.contact?.email || "",
  phone: row.contact?.phone || "",
  ...(row.buyer
    ? {
        cities: citiesText(row.buyer.cities),
        zips: (row.buyer.zips || []).join(", "),
        minPrice: row.buyer.minPrice || "",
        maxPrice: row.buyer.maxPrice || "",
        beds: row.buyer.beds || "",
        baths: row.buyer.baths || "",
      }
    : {}),
  ...(row.seller
    ? {
        street: row.seller.street || "",
        city: row.seller.city || "",
        state: row.seller.state || "",
        zip: row.seller.zip || "",
        price: row.seller.price || "",
      }
    : {}),
});

const FIELDS = {
  contact: [
    ["firstName", "First name"],
    ["lastName", "Last name"],
    ["email", "Email"],
    ["phone", "Phone"],
  ],
  buyer: [
    ["cities", "Cities (separate with ;)"],
    ["zips", "ZIP codes"],
    ["minPrice", "Lowest price"],
    ["maxPrice", "Highest price"],
    ["beds", "Beds"],
    ["baths", "Baths"],
  ],
  seller: [
    ["street", "Street address"],
    ["city", "City"],
    ["state", "State"],
    ["zip", "ZIP"],
    ["price", "Estimated price"],
  ],
};

const RowEditor = ({ row, saving, error, onSave, onCancel }) => {
  const [form, setForm] = useState(() => formOf(row));
  const groups = [FIELDS.contact, row.buyer ? FIELDS.buyer : null, row.seller ? FIELDS.seller : null].filter(Boolean);
  return (
    <form
      className="pir-editor"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(form);
      }}
    >
      {groups.map((fields, index) => (
        <div className="pir-editor__grid" key={index}>
          {fields.map(([key, label]) => (
            <label key={key}>
              <span>{label}</span>
              <input value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })} />
            </label>
          ))}
        </div>
      ))}
      {error ? (
        <p className="pi-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="pir-editor__actions">
        <button type="button" className="pir-link" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving" : "Save"}
        </button>
      </div>
    </form>
  );
};

const ProspectImportReview = ({ batch, onChange, onBack, onConfirm, confirming, onTryCsv }) => {
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const update = useUpdateImportRow();
  const select = useSelectImportRows();
  const remove = useRemoveImportRow();

  const apply = async (promise) => {
    setError(null);
    try {
      onChange(await promise);
      return true;
    } catch (err) {
      setError(err?.message || "That change didn't save. Please try again.");
      return false;
    }
  };

  const rows = batch.rows || [];
  const reviewable = rows.filter((row) => row.status === "staged" || row.status === "removed");
  const invalid = rows.filter((row) => row.status === "invalid");
  const needsLook = reviewable.filter((row) => row.issue && row.status === "staged");
  // Rows with an issue stay listed while the filter is on even after they are removed, so a removal
  // can be undone from the filtered list. The filter ends only when no such rows are left.
  const issueRows = reviewable.filter((row) => row.issue);
  const filtering = onlyIssues && issueRows.length > 0;
  // Ordered by group before paging, so "Show more" adds rows below the button, not into groups above it.
  const groupOrder = GROUPS.map((group) => group.key);
  const shown = [...(filtering ? issueRows : reviewable)].sort(
    (a, b) => groupOrder.indexOf(groupOf(a)) - groupOrder.indexOf(groupOf(b)),
  );
  const visible = shown.slice(0, limit);
  const busy = update.isPending || select.isPending || remove.isPending;

  const grouped = useMemo(
    () =>
      GROUPS.map((group) => ({ ...group, rows: visible.filter((row) => groupOf(row) === group.key) })).filter(
        (group) => group.rows.length,
      ),
    [visible],
  );

  const { buyers = 0, sellers = 0, clients = 0 } = batch.counts || {};
  const total = buyers + sellers + clients;
  const summary = [
    sellers ? plural(sellers, "prospect property", "prospect properties") : null,
    buyers ? plural(buyers, "draft Buyer Want-Listing", "draft Buyer Want-Listings") : null,
    clients ? plural(clients, "client", "clients") : null,
  ].filter(Boolean);

  const toggle = (row) =>
    apply(update.mutateAsync({ batchId: batch.id, rowId: row.id, selected: !row.selected }));

  const toggleGroup = (group) => {
    // Act on the whole group (respecting the filter), not just the rows on the current page.
    const live = shown.filter((row) => groupOf(row) === group.key && row.status === "staged");
    const allOn = live.every((row) => row.selected);
    return apply(
      select.mutateAsync({ batchId: batch.id, rowIds: live.map((row) => row.id), selected: !allOn }),
    );
  };

  if (!rows.length) {
    return (
      <div className="pir-empty">
        <h2 id="prospect-import-title" className="pi-title">
          No past buyers' properties to add right now
        </h2>
        <p className="pi-lead">
          We didn't find properties you helped buyers purchase in your MLS records that aren't already in My Properties,
          listed for sale, or sold again since.
        </p>
        <div className="pi-actions">
          <button type="button" className="pi-back" onClick={onBack}>
            <ArrowLeft size={16} aria-hidden="true" /> Back
          </button>
          <button type="button" className="btn btn-primary" onClick={onTryCsv}>
            Import from your CRM instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pir">
      <h2 id="prospect-import-title" className="pi-title">
        Review before anything is&nbsp;added
      </h2>
      <div className={`pir-banner${needsLook.length ? " has-issues" : ""}`} role="status">
        <p>
          {total ? (
            <>
              <strong>{summary.join(", ")}</strong> will be added.
            </>
          ) : (
            "Nothing is selected yet."
          )}
          {needsLook.length ? ` ${plural(needsLook.length, "row needs", "rows need")} a look.` : ""}
          {invalid.length ? ` ${plural(invalid.length, "row", "rows")} can't be imported.` : ""}
        </p>
        {needsLook.length || filtering ? (
          <label className="pir-switch">
            <input
              type="checkbox"
              checked={filtering}
              onChange={(event) => {
                setOnlyIssues(event.target.checked);
                setLimit(PAGE);
              }}
            />
            <span>Only rows that need a look</span>
          </label>
        ) : null}
      </div>

      {grouped.map((group) => {
        const live = shown.filter((row) => groupOf(row) === group.key && row.status === "staged");
        const allOn = live.length > 0 && live.every((row) => row.selected);
        return (
          <section className="pir-group" key={group.key}>
            <header className="pir-group__head">
              <label className="pir-check">
                <input
                  type="checkbox"
                  checked={allOn}
                  disabled={!live.length || busy}
                  aria-label={`Select all ${group.title}`}
                  onChange={() => toggleGroup(group)}
                />
              </label>
              <div>
                <h3>{group.title}</h3>
                <span>{group.becomes}</span>
              </div>
            </header>
            <ul className="pir-rows">
              {group.rows.map((row) => {
                const removed = row.status === "removed";
                const isEditing = editing === row.id;
                return (
                  <li
                    key={row.id}
                    className={`pir-row${row.selected ? " is-on" : ""}${removed ? " is-removed" : ""}${row.issue ? " has-issue" : ""}`}
                  >
                    <div className="pir-row__main">
                      <label className="pir-check">
                        <input
                          type="checkbox"
                          checked={row.selected && !removed}
                          disabled={removed || busy}
                          aria-label={`Include ${titleOf(row)}`}
                          onChange={() => toggle(row)}
                        />
                      </label>
                      <div className="pir-row__text">
                        <strong>{titleOf(row)}</strong>
                        {lineOf(row) ? <span>{lineOf(row)}</span> : null}
                        {row.issue ? (
                          <span className="pir-row__issue">
                            <CircleAlert size={14} aria-hidden="true" /> {row.issue}
                          </span>
                        ) : null}
                        {removed ? (
                          <span className="pir-row__issue">
                            {row.home ? "Removed. We won't suggest this home again." : "Removed from this import."}
                          </span>
                        ) : null}
                      </div>
                      <div className="pir-row__actions">
                        {removed ? (
                          <button
                            type="button"
                            className="pir-icon"
                            disabled={busy}
                            aria-label={`Restore ${titleOf(row)}`}
                            onClick={() => apply(update.mutateAsync({ batchId: batch.id, rowId: row.id, selected: true }))}
                          >
                            <Undo2 size={16} />
                          </button>
                        ) : (
                          <>
                            {!row.home ? (
                              <button
                                type="button"
                                className="pir-icon"
                                disabled={busy}
                                aria-label={`Edit ${titleOf(row)}`}
                                onClick={() => {
                                  setError(null);
                                  setEditing(isEditing ? null : row.id);
                                }}
                              >
                                <Pencil size={16} />
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="pir-icon"
                              disabled={busy}
                              aria-label={`Remove ${titleOf(row)}`}
                              onClick={() => apply(remove.mutateAsync({ batchId: batch.id, rowId: row.id }))}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {isEditing ? (
                      <RowEditor
                        row={row}
                        saving={update.isPending}
                        error={error}
                        onCancel={() => {
                          setError(null);
                          setEditing(null);
                        }}
                        onSave={async (edits) => {
                          if (await apply(update.mutateAsync({ batchId: batch.id, rowId: row.id, edits }))) {
                            setEditing(null);
                          }
                        }}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      {shown.length > limit ? (
        <button type="button" className="pir-more" onClick={() => setLimit(limit + PAGE)}>
          Show {Math.min(PAGE, shown.length - limit)} more
        </button>
      ) : null}

      {invalid.length ? (
        <details className="pir-invalid">
          <summary>{plural(invalid.length, "row", "rows")} can't be imported</summary>
          <ul>
            {invalid.slice(0, 50).map((row) => (
              <li key={row.id}>
                Row {row.row}: {row.issue}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {error && editing == null ? (
        <p className="pi-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="pi-actions pir-footer">
        <button type="button" className="pi-back" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" /> Back
        </button>
        <button type="button" className="btn btn-primary" disabled={!total || confirming || busy} onClick={onConfirm}>
          {confirming ? "Adding" : `Add ${total.toLocaleString("en-US")} to your BLS`}
        </button>
      </div>
    </div>
  );
};

export default ProspectImportReview;
